"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { StatusList } from "@/components/tools/research-workflow/check-parts";
import {
  DocumentActions,
  DocumentPreview,
} from "@/components/tools/research-workflow/document-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  isoDateToLocal,
  listFormat,
} from "@/lib/tool-kit/research-workflow/dates";
import {
  buildDmp,
  checkDmp,
  type DmpInput,
  type DmpTemplateId,
  type DmpTextField,
  EMPTY_DMP_INPUT,
  unusedFields,
} from "@/lib/tool-kit/research-workflow/dmp";
import {
  docToHtml,
  docToMarkdown,
  docToText,
} from "@/lib/tool-kit/research-workflow/document";

/** English strings. A translated content file overrides these through `ui`. */
export const DATA_MANAGEMENT_PLAN_GENERATOR_STRINGS = {
  templateLabel: "Template",
  templateOptions: {
    generic: "Generic (DCC checklist)",
    nsf: "NSF DMSP (Research.gov, 2026)",
    nsfClassic: "Classic NSF two-page plan",
    horizon: "Horizon Europe (FAIR)",
  },
  templateNotes: {
    generic:
      "Built on the eight sections of the DCC Checklist for a Data Management Plan (v4.0, 2013). If your funder or institution has its own template — DMPTool and DMPonline host many — that template comes first.",
    nsf: "Since 27 April 2026, NSF collects the plan in a Research.gov webform with these sections, answered for each of up to four data or research product categories. Draft your answers here, then enter them in the form. Data supporting NSF-funded publications must be shared at the time of publication unless the plan justifies an exception.",
    nsfClassic:
      "The two-page plan and five elements of PAPPG 24-1, used for NSF proposals submitted before 27 April 2026 and still copied by many other templates. For a new NSF proposal, use the Research.gov layout instead.",
    horizon:
      "The Horizon Europe DMP template (v1.1, 2022). It is recommended rather than mandatory, but the plan must meet Article 17 of the Grant Agreement; it is usually due as a deliverable by month 6 and updated during the project.",
  },
  projectPanel: "The project",
  dataPanel: "The data",
  documentationPanel: "Documentation and standards",
  rightsPanel: "Ethics, rights and security",
  sharingPanel: "Sharing and preservation",
  peoplePanel: "People and resources",
  titleLabel: "Project title",
  titlePlaceholder: "e.g. Urban Heat and Sleep Quality in Older Adults",
  piLabel: "Principal investigator (optional)",
  piPlaceholder: "e.g. Dr. Lena Ortiz, Riverside University",
  funderLabel: "Funder (optional)",
  funderPlaceholder: "e.g. National Science Foundation",
  grantLabel: "Grant or proposal number (optional)",
  dateLabel: "Plan date (optional)",
  doiLabel: "The repository assigns a persistent identifier (DOI)",
  fieldLabels: {
    description: "Project summary and the purpose of the data",
    dataDescription: "What data you will collect or create",
    reuse: "Existing data you will reuse (optional)",
    formats: "File formats",
    volume: "Expected volume",
    methods: "Collection, organisation and quality assurance",
    metadata: "Documentation and metadata",
    standards: "Vocabularies and data standards (interoperability)",
    ethics: "Ethics, consent and personal data",
    ip: "Ownership and licence",
    storage: "Storage and backup during the project",
    security: "Access control and security",
    preservation: "What will be kept, and for how long",
    repository: "Repository",
    sharing: "How, with whom and on what terms data will be shared",
    timeline: "When data will be shared",
    restrictions: "Restrictions on sharing, and why",
    utility: "Who else might use the data",
    otherOutputs: "Other outputs — software, protocols, samples (optional)",
    responsibilities: "Who is responsible for what",
    costs: "Costs and resources",
    otherProcedures: "Institutional or national procedures you follow",
  },
  placeholders: {
    description:
      "e.g. We will measure night-time bedroom temperature and sleep quality in 120 adults over 65 across one summer.",
    dataDescription:
      "e.g. New sensor readings (temperature every 10 minutes), wrist-actigraphy sleep data, and a 20-item questionnaire.",
    reuse:
      "e.g. Public weather-station records from the national weather service.",
    formats: "e.g. CSV for tabular data, PDF/A for consent forms",
    volume: "e.g. about 15 GB",
    methods:
      "e.g. Files named site_participant_date.csv; raw files kept read-only; cleaning scripts under version control in Git.",
    metadata:
      "e.g. A README and a codebook defining every variable, with dataset metadata in the DataCite schema.",
    standards:
      "e.g. ISO 8601 dates, SI units, variable names from an existing sleep-research codebook.",
    ethics:
      "e.g. Consent forms ask permission to share de-identified data; names and addresses are removed before deposit.",
    ip: "e.g. The university owns the data; the shared dataset will carry a CC BY 4.0 licence.",
    storage:
      "e.g. University network drive with nightly backups; laptops hold no identifiable data.",
    security:
      "e.g. Identifiable files are encrypted and open only to the three named team members.",
    preservation:
      "e.g. The de-identified dataset and codebook are kept for at least 10 years; raw identifiable files are destroyed after 5.",
    repository: "e.g. Zenodo",
    sharing:
      "e.g. De-identified data, code and codebook deposited openly for anyone to download.",
    timeline: "e.g. at the time the main paper is published",
    restrictions:
      "e.g. Exact home locations are withheld to protect participants' privacy.",
    utility:
      "e.g. Public-health researchers modelling heat risk; city planners.",
    otherOutputs:
      "e.g. Analysis code in R, shared on GitHub and archived with a DOI.",
    responsibilities:
      "e.g. The PI owns the plan; the research assistant runs backups and prepares the deposit.",
    costs:
      "e.g. No repository fee; 40 hours of assistant time to prepare the deposit, budgeted under personnel.",
    otherProcedures:
      "e.g. The university's research data management policy and its data-retention schedule.",
  },
  checkHeading: "Plan check",
  missingCount:
    "one:{count} section still needs answers.|other:{count} sections still need answers.",
  allCovered: "Every section of this template has an answer.",
  statusCovered: "Answered",
  statusMissing: "Needs answers",
  statusStandard: "Standard wording",
  statusNa: "Not applicable",
  unusedLead: "Not used by this template:",
  wordCount: "one:About {count} word.|other:About {count} words.",
  classicLimit: "The classic NSF limit was two pages.",
  previewHeading: "Your data management plan",
  previewLabel: "Data management plan preview",
  copyText: "Copy as text",
  copyFormatted: "Copy for Word or Google Docs",
  copied: "Copied!",
  downloadText: "Download .txt",
  downloadMarkdown: "Download .md",
  docTitle: "Data Management Plan",
  templateNames: {
    generic: "DCC Checklist for a Data Management Plan (v4.0)",
    nsf: "NSF Data Management and Sharing Plan (Research.gov sections)",
    nsfClassic: "NSF-style two-page plan (PAPPG 24-1 elements)",
    horizon: "Horizon Europe Data Management Plan template (v1.1)",
  },
  metaTemplate: "Template: {value}",
  metaProject: "Project: {value}",
  metaPi: "Principal investigator: {value}",
  metaFunder: "Funder: {value}",
  metaGrant: "Grant or proposal number: {value}",
  metaDate: "Date: {value}",
  missingTitle: "[project title]",
  headings: {
    dccAdmin: "1. Administrative data",
    dccCollection: "2. Data collection",
    dccCollectionWhat: "2.1 What data will you collect or create?",
    dccCollectionHow: "2.2 How will the data be collected or created?",
    dccDocumentation: "3. Documentation and metadata",
    dccDocumentationWhat:
      "3.1 What documentation and metadata will accompany the data?",
    dccEthics: "4. Ethics and legal compliance",
    dccEthicsIssues: "4.1 How will you manage any ethical issues?",
    dccEthicsIp:
      "4.2 How will you manage copyright and intellectual property rights?",
    dccStorage: "5. Storage and backup",
    dccStorageBackup:
      "5.1 How will the data be stored and backed up during the research?",
    dccStorageSecurity: "5.2 How will you manage access and security?",
    dccPreservation: "6. Selection and preservation",
    dccPreservationWhich:
      "6.1 Which data should be retained, shared and/or preserved?",
    dccPreservationPlan: "6.2 What is the long-term preservation plan?",
    dccSharing: "7. Data sharing",
    dccSharingHow: "7.1 How will you share the data?",
    dccSharingRestrictions:
      "7.2 Are any restrictions on data sharing required?",
    dccResponsibilities: "8. Responsibilities and resources",
    dccResponsibilitiesWho: "8.1 Who will be responsible for data management?",
    dccResponsibilitiesResources:
      "8.2 What resources will you require to deliver your plan?",
    nsfCategories: "Data or research product categories",
    nsfAccess: "Access policies and limitations",
    nsfStandards: "Data standards and metadata",
    nsfProvenance: "Data or research product provenance",
    nsfArchiving: "Public archiving",
    nsfTimeline: "Timeline for public accessibility",
    nsfAvailability: "Data availability",
    nsfAccountability: "Accountability",
    classicTypes: "1. Types of data and other research products",
    classicStandards: "2. Data and metadata standards",
    classicAccess: "3. Access and sharing policies",
    classicReuse: "4. Re-use, re-distribution and derivatives",
    classicArchiving: "5. Archiving and preservation of access",
    heSummary: "1. Data summary",
    heFair: "2. FAIR data",
    heFindable: "2.1 Making data findable, including provisions for metadata",
    heAccessible: "2.2 Making data accessible",
    heInteroperable: "2.3 Making data interoperable",
    heReuse: "2.4 Increase data re-use",
    heOtherOutputs: "3. Other research outputs",
    heResources: "4. Allocation of resources",
    heSecurity: "5. Data security",
    heEthics: "6. Ethics",
    heOther: "7. Other issues",
  },
  prompts: {
    description:
      "[Summarise the project and the purpose of the data in two or three sentences.]",
    dataDescription:
      "[What data will you collect or create — types, sources, and whether they are new or reused?]",
    reuse: "[Which existing data will you reuse, and for what?]",
    formats:
      "[Which file formats will you use, and do they suit sharing and long-term access?]",
    volume: "[How much data do you expect — in MB, GB or TB?]",
    methods:
      "[How will data be collected, organised, named, versioned and quality-checked?]",
    metadata:
      "[What documentation and metadata will accompany the data — a README, a codebook, a metadata standard?]",
    standards:
      "[Which vocabularies, ontologies or data standards will make the data interoperable?]",
    ethics: "[How will you handle consent, personal data and anonymisation?]",
    ip: "[Who owns the data, and under what licence will it be shared?]",
    storage:
      "[Where will data be stored during the project, and how will it be backed up?]",
    security:
      "[Who can access the data, and how will sensitive data be protected?]",
    preservation: "[Which data will be kept, and for how long?]",
    repository: "[Which repository or archive will hold the data?]",
    sharing: "[How, with whom and on what terms will the data be shared?]",
    timeline: "[When will the data be made available?]",
    restrictions:
      "[Are any restrictions on sharing needed? Say why — or say that there are none.]",
    utility: "[Who outside the project might find the data useful?]",
    otherOutputs:
      "[Which other outputs — software, protocols, models, samples — will the project produce, and how will they be shared?]",
    responsibilities: "[Who is responsible for each data management task?]",
    costs:
      "[What will data management cost — storage, repository fees, staff time — and how will it be covered?]",
    otherProcedures:
      "[Which institutional, national or funder procedures for data management will you follow?]",
  },
  leads: {
    formats: "File formats:",
    volume: "Expected volume:",
    repository: "Repository:",
    timeline: "Timeline:",
  },
  pidSentence:
    "The repository assigns each deposited dataset a persistent identifier (DOI), so it can be cited and found.",
} as const;

const TEMPLATES: readonly DmpTemplateId[] = [
  "generic",
  "nsf",
  "nsfClassic",
  "horizon",
];

const PANELS: readonly [
  (
    | "dataPanel"
    | "documentationPanel"
    | "rightsPanel"
    | "sharingPanel"
    | "peoplePanel"
  ),
  readonly DmpTextField[],
][] = [
  [
    "dataPanel",
    [
      "dataDescription",
      "reuse",
      "formats",
      "volume",
      "methods",
      "otherOutputs",
      "utility",
    ],
  ],
  ["documentationPanel", ["metadata", "standards"]],
  ["rightsPanel", ["ethics", "ip", "storage", "security"]],
  [
    "sharingPanel",
    ["sharing", "timeline", "restrictions", "repository", "preservation"],
  ],
  ["peoplePanel", ["responsibilities", "costs", "otherProcedures"]],
];

const WHITESPACE = /\s+/u;

/** Short answers get a one-line box; the rest are prose. */
const ONE_LINE: ReadonlySet<DmpTextField> = new Set([
  "formats",
  "volume",
  "repository",
  "timeline",
]);

export default function DataManagementPlanGeneratorWidget() {
  const strings = useToolUi(DATA_MANAGEMENT_PLAN_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [template, setTemplate] = useState<DmpTemplateId>("generic");
  const [input, setInput] = useState<DmpInput>(EMPTY_DMP_INPUT);
  const [date, setDate] = useState("");

  const set = <K extends keyof DmpInput>(key: K, value: DmpInput[K]) =>
    setInput((current) => ({ ...current, [key]: value }));

  const resolved = useMemo<DmpInput>(
    () => ({ ...input, date: isoDateToLocal(date, locale) }),
    [input, date, locale]
  );
  const doc = useMemo(
    () => buildDmp(resolved, template, strings),
    [resolved, template, strings]
  );
  const checks = useMemo(
    () => checkDmp(resolved, template),
    [resolved, template]
  );
  const missing = checks.filter((check) => check.status === "missing").length;
  const unused = unusedFields(template);
  const text = useMemo(() => docToText(doc), [doc]);
  const words = text.split(WHITESPACE).filter((word) => word.length > 0).length;

  const field = (key: DmpTextField) => {
    const common = {
      id: `dmp-${key}`,
      label: strings.fieldLabels[key],
      onChange: (value: string) => set(key, value),
      placeholder: strings.placeholders[key],
      value: input[key],
    };
    return ONE_LINE.has(key) ? (
      <TextField key={key} {...common} />
    ) : (
      <TextAreaField key={key} prose rows={3} {...common} />
    );
  };

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.templateLabel}
        name="dmp-template"
        onChange={setTemplate}
        options={TEMPLATES.map((id) => ({
          value: id,
          label: strings.templateOptions[id],
        }))}
        value={template}
      />
      <Notice>{strings.templateNotes[template]}</Notice>

      <Panel title={strings.projectPanel}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="dmp-title"
            label={strings.titleLabel}
            onChange={(value) => set("title", value)}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            id="dmp-pi"
            label={strings.piLabel}
            onChange={(value) => set("pi", value)}
            placeholder={strings.piPlaceholder}
            value={input.pi}
          />
          <TextField
            id="dmp-funder"
            label={strings.funderLabel}
            onChange={(value) => set("funder", value)}
            placeholder={strings.funderPlaceholder}
            value={input.funder}
          />
          <TextField
            id="dmp-grant"
            label={strings.grantLabel}
            onChange={(value) => set("grant", value)}
            value={input.grant}
          />
          <TextField
            id="dmp-date"
            label={strings.dateLabel}
            onChange={setDate}
            type="date"
            value={date}
          />
        </div>
        {field("description")}
      </Panel>
      {PANELS.map(([panel, keys]) => (
        <Panel key={panel} title={strings[panel]}>
          {keys.map(field)}
          {panel === "sharingPanel" ? (
            <Checkbox
              checked={input.doi}
              label={strings.doiLabel}
              onChange={(value) => set("doi", value)}
            />
          ) : null}
        </Panel>
      ))}

      <section className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.checkHeading}</h2>
        <p aria-live="polite" className="text-foreground text-sm">
          {missing > 0
            ? tPlural(strings.missingCount, locale, missing, {
                count: fmt(missing),
              })
            : strings.allCovered}{" "}
          {tPlural(strings.wordCount, locale, words, { count: fmt(words) })}
          {template === "nsfClassic" ? ` ${strings.classicLimit}` : null}
        </p>
        <StatusList
          items={checks.map((check) => ({
            id: check.heading,
            label:
              strings.headings[check.heading as keyof typeof strings.headings],
            status: check.status,
          }))}
          labels={{
            covered: strings.statusCovered,
            missing: strings.statusMissing,
            standard: strings.statusStandard,
            na: strings.statusNa,
          }}
        />
        {unused.length > 0 ? (
          <p className="text-muted-foreground text-sm">
            {strings.unusedLead}{" "}
            {listFormat(
              unused.map((key) => strings.fieldLabels[key]),
              locale
            )}
          </p>
        ) : null}
      </section>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
        <DocumentActions
          filename="data-management-plan"
          html={docToHtml(doc)}
          labels={strings}
          markdown={docToMarkdown(doc)}
          text={text}
        />
        <DocumentPreview doc={doc} label={strings.previewLabel} />
      </section>
    </div>
  );
}
